import React from "react";
import { experience } from "../mock";
import { Briefcase, MapPin } from "lucide-react";

const ExperienceCard = ({ exp, idx }) => {
  const isCurrent = exp.period === "Current";
  return (
    <div className="group relative rounded-2xl bg-gradient-to-br from-[#a01d3f]/10 via-[#121212] to-[#0f0f0f] border border-[#a01d3f]/30 transition-all duration-300 hover:border-[#a01d3f] hover:-translate-y-1 hover:shadow-[0_20px_50px_-18px_rgba(160,29,63,0.5)] overflow-hidden">
      {/* Vertical accent bar */}
      <span
        className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-2xl transition-colors duration-300 ${
          isCurrent
            ? "bg-[#a01d3f]"
            : "bg-[#a01d3f]/40 group-hover:bg-[#a01d3f]"
        }`}
      />

      {/* Decorative blur */}
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#a01d3f]/15 blur-3xl pointer-events-none opacity-50 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative p-6 md:p-8">
        {/* Top row: period + role index */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <span
            className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-md border ${
              isCurrent
                ? "bg-gradient-to-br from-[#a01d3f] to-[#861732] text-[#f4f1ea] border-[#a01d3f] shadow-[0_4px_12px_-2px_rgba(160,29,63,0.5)]"
                : "bg-[#1a1a1a] text-[#f4f1ea]/60 border-[#f4f1ea]/12"
            }`}
          >
            {isCurrent && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#f4f1ea] mr-1.5 animate-pulse" />
            )}
            {exp.period}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#f4f1ea]/40">
            Role 0{idx + 1} / {experience.length}
          </span>
        </div>

        {/* Role title with icon tile */}
        <div className="flex items-start gap-4 mb-5">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#a01d3f] to-[#861732] flex items-center justify-center shrink-0 shadow-[0_5px_16px_-2px_rgba(160,29,63,0.55)] group-hover:shadow-[0_8px_22px_-2px_rgba(160,29,63,0.75)] transition-shadow duration-300">
            <Briefcase size={17} strokeWidth={1.7} className="text-[#f4f1ea]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight leading-tight mb-1">
              {exp.role}
            </h3>
            <div className="flex items-center gap-4 flex-wrap text-sm text-[#f4f1ea]/65">
              <span className="font-medium text-[#f4f1ea]/85">
                {exp.company}
              </span>
              <span className="flex items-center gap-1.5">
                <MapPin size={12} />
                {exp.location}
              </span>
            </div>
          </div>
        </div>

        {/* Bullets */}
        <ul className="space-y-3 pt-5 border-t border-[#f4f1ea]/8">
          {exp.bullets.map((b, i) => (
            <li
              key={i}
              className="flex gap-3 text-sm leading-relaxed text-[#f4f1ea]/78"
            >
              <span className="font-mono text-[10px] text-[#a01d3f] mt-1.5 shrink-0">
                0{i + 1}
              </span>
              <span>
                {b.text}
                {b.metric && (
                  <>
                    {" "}
                    <span className="font-mono font-semibold text-[#f4f1ea] bg-gradient-to-br from-[#a01d3f] to-[#861732] px-2 py-0.5 rounded-md shadow-[0_2px_8px_-2px_rgba(160,29,63,0.5)]">
                      {b.metric}
                    </span>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const Experience = () => {
  return (
    <section
      id="experience"
      className="section-padding bg-[#0a0a0a] text-[#f4f1ea] relative grain overflow-hidden"
    >
      <div className="container-x relative">
        <div className="grid grid-cols-12 gap-y-10 md:gap-x-12 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#a01d3f] mb-6">
              <span className="text-[#f4f1ea]/40">[ 03 ]</span> &nbsp;—&nbsp;
              Journey
            </p>
            <h2 className="font-display text-4xl md:text-6xl font-semibold leading-[1] tracking-tight">
              Experience
              <br />
              <span className="italic font-light text-white">
                in production.
              </span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-base text-[#f4f1ea]/65 leading-relaxed max-w-md md:ml-auto">
              Five roles, three companies, a lot of incidents survived and the
              impact metrics that came out of them.
            </p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5">
          {experience.map((exp, idx) => (
            <ExperienceCard key={exp.id} exp={exp} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
